import React, { useState, useEffect } from 'react';
import {
  Shield,
  Key,
  Eye,
  EyeOff,
  Smartphone,
  LogOut,
  Download,
  Loader2,
  AlertCircle,
  Check
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { settingsService } from '../../../../services/settingsService';

// Inline error/success alert component for sections
const SectionAlert = ({ type, message, onDismiss }) => {
  if (!message) return null;

  const isError = type === 'error';
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg mb-4 ${
      isError ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'
    }`}>
      <div className="flex items-center space-x-2">
        {isError ? (
          <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
        ) : (
          <Check size={16} className="text-green-500 flex-shrink-0" />
        )}
        <span className={`text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{message}</span>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-text-secondary hover:text-text-primary text-sm">
          &times;
        </button>
      )}
    </div>
  );
};

const SecurityAccountTab = () => {
  const { user } = useAuth();

  const [changingPassword, setChangingPassword] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  // Section-specific alerts
  const [passwordAlert, setPasswordAlert] = useState({ type: null, message: null });
  const [sessionAlert, setSessionAlert] = useState({ type: null, message: null });
  const [dataAlert, setDataAlert] = useState({ type: null, message: null });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Individual show/hide toggles for each password field
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (passwordAlert.type === 'success') {
      const timer = setTimeout(() => setPasswordAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordAlert]);

  useEffect(() => {
    if (sessionAlert.type === 'success') {
      const timer = setTimeout(() => setSessionAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionAlert]);

  useEffect(() => {
    if (dataAlert.type === 'success') {
      const timer = setTimeout(() => setDataAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [dataAlert]);

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: strength, text: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { level: strength, text: 'Fair', color: 'bg-yellow-500' };
    if (strength <= 4) return { level: strength, text: 'Good', color: 'bg-green-500' };
    return { level: 5, text: 'Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  const handleChangePassword = async () => {
    setPasswordAlert({ type: null, message: null });

    // Validation
    if (!passwordForm.currentPassword) {
      setPasswordAlert({ type: 'error', message: 'Please enter your current password' });
      return;
    }

    if (!passwordForm.newPassword) {
      setPasswordAlert({ type: 'error', message: 'Please enter a new password' });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordAlert({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordAlert({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordAlert({ type: 'error', message: 'New password must be different from current password' });
      return;
    }

    try {
      setChangingPassword(true);

      await settingsService.changePasswordWithVerification(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordAlert({ type: 'success', message: 'Password updated successfully' });
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordAlert({ type: 'error', message: err.message || 'Failed to update password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSignOutOthers = async () => {
    try {
      await settingsService.signOutOtherSessions();
      setSessionAlert({ type: 'success', message: 'Signed out of other sessions' });
    } catch (err) {
      console.error('Error signing out:', err);
      setSessionAlert({ type: 'error', message: 'Failed to sign out other sessions' });
    }
  };

  const handleExportMyData = async () => {
    try {
      setExportingData(true);
      const { data } = await settingsService.exportUserData();

      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDataAlert({ type: 'success', message: 'Data exported successfully' });
    } catch (err) {
      console.error('Error exporting data:', err);
      setDataAlert({ type: 'error', message: 'Failed to export data' });
    } finally {
      setExportingData(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Security & Account</h2>
        <p className="text-text-secondary text-sm mt-1">
          Manage your password and account security
        </p>
      </div>

      {/* ==================== PASSWORD & SECURITY ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Key size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Change Password</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={passwordAlert.type}
          message={passwordAlert.message}
          onDismiss={() => setPasswordAlert({ type: null, message: null })}
        />

        <div className="max-w-md space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className="w-full px-4 py-2 pr-10 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full px-4 py-2 pr-10 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                    />
                  </div>
                  <span className={`text-xs ${passwordStrength.level <= 2 ? 'text-red-400' : passwordStrength.level <= 3 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {passwordStrength.text}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-text-primary focus:outline-none focus:border-primary ${
                  passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                    ? 'border-red-500'
                    : 'border-border'
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {changingPassword ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Updating Password...
              </>
            ) : (
              <>
                <Key size={14} className="mr-2" />
                Update Password
              </>
            )}
          </button>
        </div>

        {/* 2FA Placeholder */}
        <div className="mt-6 p-4 bg-background border border-border rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Smartphone size={20} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">Two-Factor Authentication</p>
                <p className="text-text-secondary text-xs">Add an extra layer of security to your account</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-border/50 text-text-secondary rounded-full text-xs">Coming Soon</span>
          </div>
        </div>
      </div>

      {/* ==================== SESSIONS ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Sessions & Devices</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={sessionAlert.type}
          message={sessionAlert.message}
          onDismiss={() => setSessionAlert({ type: null, message: null })}
        />

        <div className="p-4 bg-background border border-border rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-primary text-sm font-medium">Current Session</p>
              <p className="text-text-secondary text-xs">This device</p>
            </div>
            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
          </div>
        </div>

        <button
          onClick={handleSignOutOthers}
          className="px-4 py-2 bg-surface text-text-primary rounded-lg text-sm font-medium hover:bg-surface-light transition-colors flex items-center"
        >
          <LogOut size={14} className="mr-2" />
          Sign Out Other Sessions
        </button>
      </div>

      {/* ==================== DATA & PRIVACY ==================== */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <Download size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Data & Privacy</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={dataAlert.type}
          message={dataAlert.message}
          onDismiss={() => setDataAlert({ type: null, message: null })}
        />

        <div className="space-y-3">
          <button
            onClick={handleExportMyData}
            disabled={exportingData}
            className="w-full md:w-auto px-4 py-2 bg-surface text-text-primary rounded-lg text-sm font-medium hover:bg-surface-light transition-colors flex items-center"
          >
            {exportingData ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
            Export My Data
          </button>

          <p className="text-xs text-text-secondary">
            Export your personal data in JSON format for GDPR compliance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecurityAccountTab;
